import {INVOICE_ENTRY_TYPE} from '../core/invoiceAutoSend'
import {Invoice, InvoicePayment} from '../core/_models'

/** «فرم قیمت تمام شده پیشرفته» in the form selector (currentUser.inmd). */
export const ADVANCED_COST_FORM_TYPE = 3
export const FULL_FORM_TYPE = 0

// The advanced form rebuilds the goods with the automatic-invoice engine, so it may only edit an
// invoice the engine built: the advanced form itself or the automatic Excel. POS invoices share their
// entry type with API v1's classic POS invoices, which the engine did not build, so every POS invoice
// is edited in the full form (it keeps the rows and payments as they are).
const ENGINE_BUILT_ENTRY_TYPES: number[] = [
  INVOICE_ENTRY_TYPE.webFormAdvancedCostPrice,
  INVOICE_ENTRY_TYPE.automaticInvoice,
]

const UNSENT_PHASE = 1
const ORIGINAL_SUBJECT = 1

/**
 * Whether an existing invoice may be edited in the advanced form: built by the engine, still
 * unsent and an original, and opened for a plain edit (a correction must keep the reference's
 * goods). The server applies the same rule; any other invoice opens in the full form, which keeps
 * its goods and payments. A new invoice may always use it.
 */
export const canEditWithAdvancedCostForm = (invoice?: Invoice | null, variableD?: unknown) => {
  if (!invoice?.id) return true
  if (variableD !== undefined && variableD !== null) return false
  return (
    ENGINE_BUILT_ENTRY_TYPES.includes(Number(invoice.ent)) &&
    (invoice.ph?.i ?? UNSENT_PHASE) === UNSENT_PHASE &&
    (invoice.sbji || ORIGINAL_SUBJECT) === ORIGINAL_SUBJECT
  )
}

/** The form an edit actually opens in: the full form instead of an advanced one it may not use. */
export const resolveEditFormType = (
  formType: number,
  invoice?: Invoice | null,
  variableD?: unknown
) => {
  const requested = Number(formType) || FULL_FORM_TYPE
  return requested === ADVANCED_COST_FORM_TYPE && !canEditWithAdvancedCostForm(invoice, variableD)
    ? FULL_FORM_TYPE
    : requested
}

/** The pattern an edit keeps: the invoice's own (the list passes 0 while editing). */
export const getAdvancedFormTemplate = (invoice: Invoice | null | undefined, template: number) =>
  invoice?.id ? Number(invoice.tmpi) || Number(template) || 1 : Number(template) || 1

export const getInvoicePosPayments = (invoice?: Invoice | null): InvoicePayment[] =>
  (invoice?.pym || []).filter((payment) => Number(payment.typ) === 4)

const toLatinDigits = (value: string) =>
  value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))

/**
 * The amount inside a POS transaction option («… - ۱٬۵۰۰٬۰۰۰ ریال - رهگیری …»), which the search
 * service formats with the server's culture. Undefined when the label carries none.
 */
export const parsePosTransactionAmount = (label: unknown) => {
  if (typeof label !== 'string') return undefined
  const match = label.match(/([0-9۰-۹٠-٩][0-9۰-۹٠-٩,،٬.]*)\s*ریال/)
  if (!match) return undefined
  const amount = Number(toLatinDigits(match[1]).replace(/[,،٬.]/g, ''))
  return Number.isFinite(amount) && amount > 0 ? amount : undefined
}

const INSURANCE_TEMPLATE = 13

const isInsuranceId = (value: string) => /^\d{9,12}$/.test(value.trim())

/** Pattern 13 needs the policy id (9–12 digits); the endorsement id is optional, same length. */
export const getInsuranceIdsError = (
  template: number,
  insPolicyNo: string,
  extInsPolicyNo: string
) => {
  if (Number(template) !== INSURANCE_TEMPLATE) return ''
  if (!isInsuranceId(insPolicyNo)) {
    return 'شناسه یکتای بیمه‌نامه را وارد کنید؛ ۹ تا ۱۲ رقم.'
  }
  if (extInsPolicyNo.trim() && !isInsuranceId(extInsPolicyNo)) {
    return 'شناسه یکتای الحاقیه باید ۹ تا ۱۲ رقم باشد.'
  }
  return ''
}

export const isInsuranceTemplate = (template: number) => Number(template) === INSURANCE_TEMPLATE
